import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
// import Sidebar from '../components/layout/Sidebar';
import RightBar from '../components/layout/RightBar';
import SearchBar from '../components/search/SearchBar';
import SearchResults from '../components/search/SearchResults';
import { 
  search, 
  searchUsers, 
  searchPosts, 
  searchCommunities, 
  getTrendingSearches 
} from '../api/search';

/**
 * @returns {JSX.Element} 
 */
const Search = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';
  const initialType = searchParams.get('type') || '';
  
  // Get all filter parameters from URL
  const getInitialFilters = () => {
    const filterParams = {};
    searchParams.forEach((value, key) => {
      // Skip non-filter parameters
      if (!['q', 'type', 'page'].includes(key)) {
        filterParams[key] = value;
      }
    });
    return filterParams;
  };
  
  const initialFilters = getInitialFilters();
  
  const [results, setResults] = useState({
    users: [],
    posts: [],
    communities: [],
  });
  const [trendingData, setTrendingData] = useState({
    trendingTopics: [],
    trendingUsers: [],
    trendingPosts: [],
    trendingCommunities: [],
  });
  const [loading, setLoading] = useState(false);
  const [trendingLoading, setTrendingLoading] = useState(false);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    pages: 1,
    total: 0,
  });
  const [activeFilters, setActiveFilters] = useState(initialFilters);
  
  // Fetch trending data on component mount
  useEffect(() => {
    fetchTrendingData();
  }, []);
  
  // Perform search when query, type, or filters change
  useEffect(() => {
    if (initialQuery) {
      const currentPage = parseInt(searchParams.get('page') || '1', 10);
      performSearch(initialQuery, initialType, currentPage, initialFilters);
    }
  }, [initialQuery, initialType, searchParams]);
  
  // Fetch trending data
  const fetchTrendingData = async () => {
    try {
      setTrendingLoading(true);
      
      const response = await getTrendingSearches();
      
      setTrendingData({
        trendingTopics: response.data.data.trendingHashtags || [],
        trendingUsers: response.data.data.trendingUsers || [],
        trendingPosts: response.data.data.trendingPosts || [],
        trendingCommunities: response.data.data.trendingCommunities || [],
      });
    } catch (error) {
      console.error('Error fetching trending data:', error);
    } finally {
      setTrendingLoading(false);
    }
  };
  
  // Search handler
  const handleSearch = (query, type = '', filters = {}) => {
    // Update active filters
    setActiveFilters(filters);
    
    // Build URL parameters
    const params = new URLSearchParams();
    params.append('q', query);
    params.set('page', '1'); // Reset to page 1 for new searches
    
    if (type) {
      params.append('type', type);
    }
    
    // Add filters to URL
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        params.append(key, value);
      }
    });
    
    setSearchParams(params);
  };
  
  // Filter change handler
  const handleFilterChange = (key, value) => {
    const newFilters = { ...activeFilters };
    
    if (key === 'reset') {
      // Reset all filters
      Object.keys(newFilters).forEach(k => {
        delete newFilters[k];
      });
    } else {
      // Update or remove specific filter
      if (value === '') {
        delete newFilters[key];
      } else {
        newFilters[key] = value;
      }
    }
    
    // Update search with new filters
    handleSearch(initialQuery, initialType, newFilters);
  };
  
  // Page change handler
  const handlePageChange = (page) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', page.toString());
    setSearchParams(params);
  };
  
  // Perform search
  const performSearch = async (query, type, page = 1, filters = {}) => {
    if (!query) return;
    
    try {
      setLoading(true);
      setError('');
      
      // Build query parameters
      const queryParams = {
        q: query,
        page,
        limit: 10,
        ...filters
      };
      
      let response;
      
      if (type === 'users') {
        response = await searchUsers(query, { ...queryParams, type: undefined });
      } else if (type === 'posts') {
        response = await searchPosts(query, { ...queryParams, type: undefined });
      } else if (type === 'communities') {
        response = await searchCommunities(query, { ...queryParams, type: undefined });
      } else {
        response = await search(queryParams);
      }
      
      // Process response based on search type
      if (type === 'users') {
        setResults({
          users: response.data.data.users || [],
          posts: [],
          communities: [],
        });
      } else if (type === 'posts') {
        setResults({
          users: [],
          posts: response.data.data.posts || [],
          communities: [],
        });
      } else if (type === 'communities') {
        setResults({
          users: [],
          posts: [],
          communities: response.data.data.communities || [],
        });
      } else {
        // For general search, all types are returned
        setResults({
          users: response.data.data.users || [],
          posts: response.data.data.posts || [],
          communities: response.data.data.communities || [],
        });
      }
      
      // Set pagination data
      setPagination({
        page: response.data.pagination.page,
        pages: response.data.pagination.pages,
        total: response.data.pagination.total,
      });
      
    } catch (err) {
      console.error('Search error:', err);
      setError('An error occurred while searching. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="bg-gray-100 min-h-screen">
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Left sidebar */}
          
          {/* Main content */}
          <div className="flex-1 space-y-6">
            {/* Error message */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}
            
            {/* Search form */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <SearchBar
                initialQuery={initialQuery}
                initialType={initialType}
                initialFilters={activeFilters}
                onSearch={handleSearch}
              />
            </div>
            
            {/* Search results */}
            <SearchResults
              query={initialQuery}
              users={results.users}
              posts={results.posts}
              communities={results.communities}
              trendingTopics={trendingData.trendingTopics}
              trendingUsers={trendingData.trendingUsers}
              loading={loading}
              pagination={pagination}
              activeType={initialType}
              filters={activeFilters}
              onPageChange={handlePageChange}
              onFilterChange={handleFilterChange}
            />
          </div>
          
          {/* Right sidebar */}
          <div className="hidden lg:block lg:w-80">
            <RightBar />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Search;